import { useIntl } from "react-intl";

import {
  HorizontalRectangleContainer,
  HorizontalRectangleImage,
  HorizontalRectangleMask,
} from "./styles";

import Labels from "../Labels";
import Logo from "../Logo";

import {
  getColor,
  pickHorizontalRectangleDefaultPositions,
  pickHorizontalRectangleDefaultSizes,
  style,
} from "../shapes-utils";

const ratio = 300 / 157;

export function HorizontalRectangle01({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 1,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 1,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={1}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2066_3633)">
          <path
            fill={getColor(variantColor).primary}
            d="M1200 0H0v53.848c38.223-7.02 78.133-9.323 118.961-6.288 241.824 17.983 424.184 216.495 407.312 443.391-3.628 48.772-16.164 94.883-36.015 137.049H1200z"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            fillOpacity={getColor(variantColor).fillOpacity}
            fillRule="evenodd"
            d="M49.817 928.352c279.733 20.801 522.129-172.498 541.407-431.746S399.362 10.334 119.629-10.467-402.5 162.031-421.778 421.278-229.916 907.55 49.817 928.352m5.36-58.259C297 888.075 506.713 718.717 523.585 491.82 540.459 264.924 358.1 66.411 116.276 48.429S-335.262 199.805-352.134 426.7s165.487 425.409 407.31 443.392"
            clipRule="evenodd"
            style={style}
          ></path>
          <path
            style={style}
            stroke={getColor(variantColor).secondary}
            strokeWidth="13.798"
            d="M522.392 485.44C505.833 708.13 299.817 875.074 61.374 857.343c-238.444-17.73-417.502-213.309-400.942-436C-323.009 198.653-116.993 31.709 121.45 49.44c238.444 17.73 417.502 213.31 400.942 436Z"
          ></path>
        </g>
      </HorizontalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle01"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}

export function HorizontalRectangle02({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 2,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 2,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={2}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2060_5418)">
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M491.653-86.999C491.653-4.289 424.218 63 341.327 63S191-4.287 191-86.999 258.436-237 341.327-237s150.326 67.293 150.326 150.001"
          />
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M598.156 248.687a3.45 3.45 0 1 1-6.899 0 3.45 3.45 0 0 1 6.899 0m-26.961 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.962 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.335 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m80.258-25.077a3.45 3.45 0 1 1-6.899 0 3.45 3.45 0 0 1 6.899 0m-26.961 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.962 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.335 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m80.258-25.081a3.448 3.448 0 1 1 0-.004zm-26.961 0a3.449 3.449 0 1 1 0-.004zm-26.962 0a3.449 3.449 0 1 1 0-.004zm-26.335 0a3.449 3.449 0 1 1 0-.004zm80.258-25.081a3.448 3.448 0 1 1 0-.004zm-26.961 0a3.449 3.449 0 1 1 0-.004zm-26.962 0a3.449 3.449 0 1 1 0-.004zm-26.335 0a3.449 3.449 0 1 1 0-.004z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).primary}
            fillRule="evenodd"
            d="M730 0H0v628h730z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M491.653-86.999C491.653-4.289 424.218 63 341.327 63S191-4.287 191-86.999 258.436-237 341.327-237s150.326 67.293 150.326 150.001"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M598.156 248.687a3.45 3.45 0 1 1-6.899 0 3.45 3.45 0 0 1 6.899 0m-26.961 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.962 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.335 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m80.258-25.077a3.45 3.45 0 1 1-6.899 0 3.45 3.45 0 0 1 6.899 0m-26.961 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.962 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m-26.335 0a3.449 3.449 0 1 1-6.898 0 3.449 3.449 0 0 1 6.898 0m80.258-25.081a3.448 3.448 0 1 1 0-.004zm-26.961 0a3.449 3.449 0 1 1 0-.004zm-26.962 0a3.449 3.449 0 1 1 0-.004zm-26.335 0a3.449 3.449 0 1 1 0-.004zm80.258-25.081a3.448 3.448 0 1 1 0-.004zm-26.961 0a3.449 3.449 0 1 1 0-.004zm-26.962 0a3.449 3.449 0 1 1 0-.004zm-26.335 0a3.449 3.449 0 1 1 0-.004z"
            clipRule="evenodd"
          ></path>
        </g>
      </HorizontalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle02"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}

export function HorizontalRectangle03({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 3,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 3,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={3}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2060_5478)">
          <path
            fill="url(#paint0_linear_2060_5478)"
            d="M600 136.424-496-517v839L600 975.424z"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).primary}
            fillRule="evenodd"
            d="M1200 0H0v628h631V351.409L768.342 105H1200z"
            clipRule="evenodd"
            style={style}
          ></path>
          <path
            stroke={getColor(variantColor).secondary}
            strokeWidth="2"
            d="M1251-39 1080 78"
            style={style}
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            style={style}
            d="m551.002 228.5-30 19.5v-39zM241 50l-23 15V35zM255 554.5 236 567v-25z"
          ></path>
        </g>
      </HorizontalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle03"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}

export function HorizontalRectangle04({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 4,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 4,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={4}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2061_5730)">
          <path
            fill={getColor(variantColor).primary}
            style={style}
            d="M461.988 37.93C526.5 296.71 882.609 567.168 1068.61 509.027c44.1-13.781 89.16-44.445 131.39-86.652V628H0V0h454.172c2.004 12.395 4.601 25.035 7.816 37.93"
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            style={style}
            d="M1094.34-455.699c-77.49-14.487-164.268-9.853-254.517 12.653C581.048-378.514 397.48-220.85 461.99 37.93c41.012 164.523 199.888 333.766 357.983 420.287C629.046 419.992 377.225 198.486 323.99-15.07c-64.51-258.78 119.058-416.444 377.833-480.976 149.041-37.167 288.616-25.593 392.517 40.347"
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            style={style}
            fillRule="evenodd"
            d="M197.877 279.549a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.951 0a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.95 0a3.124 3.124 0 1 0 0 6.249 3.124 3.124 0 0 0 0-6.249m-22.951 0a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.951 0a3.124 3.124 0 1 0 0 6.249 3.124 3.124 0 0 0 0-6.249m-22.95 0a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m114.753-18.387a3.123 3.123 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.951 0a3.123 3.123 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.95 0a3.124 3.124 0 1 0 0 6.247 3.124 3.124 0 0 0 0-6.247m-22.951 0a3.123 3.123 0 1 0 0 6.248 3.125 3.125 0 0 0 0-6.248m-22.951 0a3.124 3.124 0 1 0 0 6.247 3.124 3.124 0 0 0 0-6.247m-22.95 0a3.123 3.123 0 1 0-.001 6.246 3.123 3.123 0 0 0 0-6.246m114.753-18.387a3.123 3.123 0 1 0-.002 6.246 3.123 3.123 0 0 0 .002-6.246m-22.951 0a3.123 3.123 0 1 0-.002 6.246 3.123 3.123 0 0 0 .002-6.246m-22.95 0a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.951 0a3.123 3.123 0 1 0 0 6.247 3.124 3.124 0 1 0 0-6.247m-22.951 0a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.95 0a3.123 3.123 0 1 0-.001 6.247 3.123 3.123 0 0 0 0-6.247m114.753-18.388a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.951 0a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.95 0a3.125 3.125 0 1 0 .001 6.25 3.125 3.125 0 0 0-.001-6.25m-22.951 0a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.951 0a3.125 3.125 0 1 0 .001 6.25 3.125 3.125 0 0 0-.001-6.25m-22.95 0a3.124 3.124 0 1 0 0 6.249 3.124 3.124 0 0 0 0-6.249m114.753-12.139a3.124 3.124 0 1 0-.001-6.247 3.124 3.124 0 0 0 .001 6.247M174.926 206a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.95 0a3.124 3.124 0 1 0 0 6.249 3.124 3.124 0 0 0 0-6.249m-22.951 0a3.124 3.124 0 1 0 0 6.248 3.124 3.124 0 0 0 0-6.248m-22.951 0a3.124 3.124 0 1 0 0 6.249 3.124 3.124 0 0 0 0-6.249m-19.826 3.124a3.124 3.124 0 1 1-6.248 0 3.124 3.124 0 0 1 6.248 0M-69 60c-39.7 0-72 32.3-72 72s32.3 72 72 72 72-32.3 72-72-32.3-72-72-72m0 204c-72.784 0-132-59.216-132-132S-141.784 0-69 0 63 59.216 63 132 3.784 264-69 264"
            clipRule="evenodd"
          ></path>
        </g>
      </HorizontalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle04"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}

export function HorizontalRectangle05({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 5,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 5,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={5}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2064_5806)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M1200 0H0v628h589.074c-44.976-148.273-28.133-324.73 40.625-402.203 96.348-108.563 422.231-75.492 570.301 60.715z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M593.057 290.706c-53.735 43.623-90.687 93.25-98.074 142.199-23.46 155.471 164.788 429.911 375.186 474.877 210.401 44.966 351.371-92.753 395.871-303.25.63-3.009 1.25-6.014 1.85-9.014-24.12 56.375-64.38 111.609-115.53 162.857-143.31 143.577-325.531 180.08-468.834 36.503-121.344-121.576-145.623-365.084-90.469-504.172M871.29 163.242c123.817 7.927 260.5 54.54 338.93 133.117 29.17 29.224 50 59.744 63.48 91.032-21.26-112.74-90-198.855-223.13-227.307-54.639-11.678-116.792-9.375-179.28 3.158"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M1172.62 128.517a3.38 3.38 0 0 0-3.38 3.383 3.38 3.38 0 0 0 3.38 3.382c1.87 0 3.38-1.514 3.38-3.382a3.38 3.38 0 0 0-3.38-3.383m0-24.89a3.38 3.38 0 0 0-3.38 3.383 3.38 3.38 0 0 0 3.38 3.382c1.87 0 3.38-1.514 3.38-3.382a3.38 3.38 0 0 0-3.38-3.383m-24.85 0a3.38 3.38 0 0 0-3.38 3.383 3.38 3.38 0 0 0 3.38 3.382c1.87 0 3.38-1.514 3.38-3.382a3.38 3.38 0 0 0-3.38-3.383m24.85-19.906a3.38 3.38 0 0 0-3.38 3.381 3.38 3.38 0 0 0 3.38 3.383c1.87 0 3.38-1.514 3.38-3.383a3.38 3.38 0 0 0-3.38-3.381m-24.85 0a3.38 3.38 0 0 0-3.38 3.381 3.38 3.38 0 0 0 3.38 3.383c1.87 0 3.38-1.514 3.38-3.383a3.38 3.38 0 0 0-3.38-3.381m-24.85 0a3.385 3.385 0 0 0-3.38 3.381 3.385 3.385 0 0 0 6.77 0 3.387 3.387 0 0 0-3.39-3.381m49.7-19.907a3.38 3.38 0 0 0-3.38 3.382 3.38 3.38 0 1 0 6.76 0 3.38 3.38 0 0 0-3.38-3.382m-24.85 0a3.38 3.38 0 0 0-3.38 3.382 3.38 3.38 0 1 0 6.76 0 3.38 3.38 0 0 0-3.38-3.382m-24.85 0a3.385 3.385 0 0 0-3.38 3.382 3.385 3.385 0 0 0 6.77 0 3.387 3.387 0 0 0-3.39-3.382m-24.84 0a3.387 3.387 0 0 0-3.39 3.382 3.385 3.385 0 0 0 6.77 0 3.385 3.385 0 0 0-3.38-3.382m74.54-19.907a3.38 3.38 0 0 0-3.38 3.382 3.38 3.38 0 0 0 3.38 3.382c1.87 0 3.38-1.514 3.38-3.382a3.38 3.38 0 0 0-3.38-3.382m-24.85 0a3.38 3.38 0 0 0-3.38 3.382 3.38 3.38 0 0 0 3.38 3.382c1.87 0 3.38-1.514 3.38-3.382a3.38 3.38 0 0 0-3.38-3.382m-24.85 0a3.386 3.386 0 0 0-3.38 3.382 3.385 3.385 0 0 0 6.77 0 3.39 3.39 0 0 0-3.39-3.382m-24.84 0a3.39 3.39 0 0 0-3.39 3.382 3.385 3.385 0 0 0 6.77 0 3.386 3.386 0 0 0-3.38-3.382m-24.85 0a3.38 3.38 0 0 0-3.38 3.382 3.38 3.38 0 0 0 3.38 3.382c1.87 0 3.38-1.514 3.38-3.382a3.38 3.38 0 0 0-3.38-3.382m99.39-13.142a3.38 3.38 0 0 0 3.38-3.383 3.38 3.38 0 1 0-6.76 0 3.38 3.38 0 0 0 3.38 3.383M1147.77 24a3.38 3.38 0 0 0-3.38 3.382 3.38 3.38 0 1 0 6.76 0 3.38 3.38 0 0 0-3.38-3.382m-24.85 0a3.385 3.385 0 0 0-3.38 3.382 3.385 3.385 0 0 0 6.77 0 3.39 3.39 0 0 0-3.39-3.382m-24.84 0a3.39 3.39 0 0 0-3.39 3.382 3.385 3.385 0 0 0 6.77 0 3.385 3.385 0 0 0-3.38-3.382m-24.85 0a3.38 3.38 0 0 0-3.38 3.382 3.38 3.38 0 1 0 6.76 0 3.38 3.38 0 0 0-3.38-3.382m-21.47 3.382a3.38 3.38 0 1 1-6.76 0 3.38 3.38 0 1 1 6.76 0M521.296 71l-14.792 14.792L491.704 71 486 76.704l14.8 14.792-14.8 14.8 5.704 5.704 14.8-14.8 14.792 14.8 5.704-5.704-14.796-14.8L527 76.704z"
            clipRule="evenodd"
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            fillOpacity="0.2"
            fillRule="evenodd"
            d="M250.656 545.601a22.1 22.1 0 0 0 15.734 6.518 22.1 22.1 0 0 0 15.734-6.517l3.437-3.437a18.14 18.14 0 0 1 12.907-5.346c4.875 0 9.459 1.899 12.906 5.346l1.533 1.533a22.1 22.1 0 0 0 15.734 6.517 22.1 22.1 0 0 0 15.735-6.517l1.222-1.223c7.117-7.115 18.696-7.116 25.813 0 8.676 8.676 22.792 8.676 31.469 0l1.139-1.14c7.119-7.115 18.698-7.115 25.813 0a2 2 0 1 0 2.828-2.828c-8.676-8.676-22.793-8.675-31.469 0l-1.139 1.14c-7.118 7.118-18.697 7.117-25.813 0-8.676-8.676-22.794-8.676-31.469 0l-1.222 1.222a18.13 18.13 0 0 1-12.907 5.346 18.13 18.13 0 0 1-12.906-5.346l-1.533-1.532a22.1 22.1 0 0 0-15.734-6.518 22.1 22.1 0 0 0-15.735 6.517l-3.437 3.437a18.13 18.13 0 0 1-12.906 5.346 18.13 18.13 0 0 1-12.906-5.346l-2.429-2.429c-8.676-8.676-22.793-8.677-31.469 0a2 2 0 1 0 2.828 2.828c7.118-7.116 18.697-7.115 25.812 0zm0 48a22.1 22.1 0 0 0 15.734 6.518 22.1 22.1 0 0 0 15.734-6.517l3.437-3.437a18.14 18.14 0 0 1 12.907-5.346c4.875 0 9.459 1.899 12.906 5.346l1.533 1.533a22.1 22.1 0 0 0 15.734 6.517 22.1 22.1 0 0 0 15.735-6.517l1.222-1.223c7.117-7.115 18.696-7.116 25.813 0 8.676 8.676 22.792 8.676 31.469 0l1.139-1.14c7.119-7.115 18.698-7.115 25.813 0a2 2 0 1 0 2.828-2.828c-8.676-8.676-22.793-8.675-31.469 0l-1.139 1.14c-7.118 7.118-18.697 7.117-25.813 0-8.676-8.676-22.794-8.676-31.469 0l-1.222 1.222a18.13 18.13 0 0 1-12.907 5.346 18.13 18.13 0 0 1-12.906-5.346l-1.533-1.532a22.1 22.1 0 0 0-15.734-6.518 22.1 22.1 0 0 0-15.735 6.517l-3.437 3.437a18.13 18.13 0 0 1-12.906 5.346 18.13 18.13 0 0 1-12.906-5.346l-2.429-2.429c-8.676-8.676-22.793-8.677-31.469 0a2 2 0 1 0 2.828 2.828c7.118-7.116 18.697-7.115 25.812 0z"
            clipRule="evenodd"
          ></path>
          <g fill={getColor(variantColor).secondary} opacity="0.23">
            <path
              style={style}
              d="M682.911.62c.782-.622.784-.62.784-.62l.004.005.016.02.061.077.065.084.167.216c.203.264.502.657.887 1.176a148 148 0 0 1 3.267 4.608c2.768 4.053 6.619 10.041 10.959 17.8 8.681 15.516 19.327 38.12 27.199 66.484 6.409 23.094 10.978 50.004 11.152 80.012q-1 .217-1.997.439c-.132-29.992-4.687-56.871-11.083-79.917-7.821-28.182-18.398-50.637-27.016-66.042-4.309-7.702-8.127-13.64-10.866-17.648a146 146 0 0 0-3.222-4.545c-.378-.51-.669-.893-.865-1.148l-.222-.286-.055-.07-.013-.016-.003-.004zM563.27 439.001c-36.963 15.085-77.392 24.129-117.844 27.316-52.789 4.16-105.537-1.668-150.541-17.022-45.012-15.357-82.202-40.216-104-74.066-44.065-68.431-92.227-88.107-129.487-90.562-18.602-1.225-34.434 1.845-45.618 5.221-5.593 1.689-10.027 3.455-13.068 4.802a76 76 0 0 0-3.488 1.644c-.398.201-.701.361-.907.471l-.131.07-.103.057-.062.034-.016.009-.005.003s-.002.001.489.872l.49.871.003-.001.012-.007.052-.028.075-.042.14-.075q.29-.156.865-.449a74 74 0 0 1 3.396-1.601c2.975-1.317 7.332-3.053 12.836-4.715 11.01-3.324 26.598-6.347 44.908-5.14 36.561 2.408 84.177 21.691 127.938 89.649 22.115 34.344 59.748 59.425 105.035 74.876 45.295 15.453 98.32 21.301 151.344 17.123 40.303-3.175 80.651-12.149 117.627-27.129q.029-1.092.06-2.181"
            ></path>
          </g>
        </g>
      </HorizontalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle05"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}

export function HorizontalRectangle06({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 6,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 6,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={6}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2060_5602)">
          <path
            fill={getColor(variantColor).primary}
            fillRule="evenodd"
            d="M362.668 628c2.867-9.325 5.337-19.42 7.366-30.361 42.408-228.625 64.463-276.768 237.409-312.159C732.481 259.893 752.625 91.39 754.191 0H1200v628z"
            clipRule="evenodd"
            style={style}
          ></path>
          <ellipse
            cx="1300.5"
            cy="314"
            stroke={getColor(variantColor).secondary}
            strokeWidth="50"
            rx="134.5"
            ry="127"
            style={style}
          ></ellipse>
          <path
            fill={getColor(variantColor).secondary}
            style={style}
            fillRule="evenodd"
            d="M772.106 138.724a579 579 0 0 0 10.601-55.981c7.99-56.688 6.844-101.937 6.827-102.529l-.038-1.353L-33.909-161.75l-1.462-.25-172.817 1226.14 134.193 13.83.281.03.273-.09c.639-.22 15.88-5.55 37.294-34.66 19.586-26.63 48.302-79.214 73.872-178.56 23.419-90.985 86.448-102.067 153.178-113.8h.001c72.774-12.795 148.024-26.026 177.488-144.141 29.444-118.035 49.378-181.388 83.034-222.18 32.53-39.429 78.928-58.42 171.187-70.072 40.189-5.076 73.987-25.888 100.457-61.859 21.107-28.684 37.604-67.014 49.036-113.914M-38.101 1040.76c-19.433 26.55-33.847 33.06-35.888 33.88l-130.811-13.48L-32.935-158.229 786.626-18.275c.168 10.682.554 82.756-17.367 156.239-17.758 72.81-57.662 161.97-146.928 173.244-47.025 5.939-79.701 13.214-105.95 23.585-28.205 11.144-49.526 26.253-67.092 47.544-34.076 41.303-54.147 104.999-83.723 223.564C336.623 721.929 262.339 734.99 190.5 747.621c-34.036 5.983-66.184 11.636-93.716 27.545-30.876 17.841-51.116 46.839-61.879 88.654-25.333 98.424-53.684 150.55-73.006 176.94"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M904.807 153.216a16 16 0 1 1 18.061 13.63c-8.751 1.223-16.837-4.879-18.061-13.63"
            opacity="0.502"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity="0.2"
            fillRule="evenodd"
            d="M487.656 499.601a22.1 22.1 0 0 0 15.734 6.518 22.1 22.1 0 0 0 15.734-6.517l3.437-3.437a18.14 18.14 0 0 1 12.907-5.346c4.875 0 9.459 1.899 12.906 5.346l1.533 1.533a22.1 22.1 0 0 0 15.734 6.517 22.1 22.1 0 0 0 15.735-6.517l1.222-1.223c7.117-7.115 18.696-7.116 25.813 0 8.676 8.676 22.792 8.676 31.469 0l1.139-1.14c7.119-7.115 18.698-7.115 25.813 0a2 2 0 1 0 2.828-2.828c-8.676-8.676-22.793-8.675-31.469 0l-1.139 1.14c-7.118 7.118-18.697 7.117-25.813 0-8.676-8.676-22.794-8.676-31.469 0l-1.222 1.222a18.13 18.13 0 0 1-12.907 5.346 18.13 18.13 0 0 1-12.906-5.346l-1.533-1.532a22.1 22.1 0 0 0-15.734-6.518 22.1 22.1 0 0 0-15.735 6.517l-3.437 3.437a18.13 18.13 0 0 1-12.906 5.346 18.13 18.13 0 0 1-12.906-5.346l-2.429-2.429c-8.676-8.676-22.793-8.677-31.469 0a2 2 0 1 0 2.828 2.828c7.118-7.116 18.697-7.115 25.812 0zm0 48a22.1 22.1 0 0 0 15.734 6.518 22.1 22.1 0 0 0 15.734-6.517l3.437-3.437a18.14 18.14 0 0 1 12.907-5.346c4.875 0 9.459 1.899 12.906 5.346l1.533 1.533a22.1 22.1 0 0 0 15.734 6.517 22.1 22.1 0 0 0 15.735-6.517l1.222-1.223c7.117-7.115 18.696-7.116 25.813 0 8.676 8.676 22.792 8.676 31.469 0l1.139-1.14c7.119-7.115 18.698-7.115 25.813 0a2 2 0 1 0 2.828-2.828c-8.676-8.676-22.793-8.675-31.469 0l-1.139 1.14c-7.118 7.118-18.697 7.117-25.813 0-8.676-8.676-22.794-8.676-31.469 0l-1.222 1.222a18.13 18.13 0 0 1-12.907 5.346 18.13 18.13 0 0 1-12.906-5.346l-1.533-1.532a22.1 22.1 0 0 0-15.734-6.518 22.1 22.1 0 0 0-15.735 6.517l-3.437 3.437a18.13 18.13 0 0 1-12.906 5.346 18.13 18.13 0 0 1-12.906-5.346l-2.429-2.429c-8.676-8.676-22.793-8.677-31.469 0a2 2 0 1 0 2.828 2.828c7.118-7.116 18.697-7.115 25.812 0z"
            clipRule="evenodd"
          ></path>
          <circle
            style={style}
            cx="1157"
            cy="569"
            r="16"
            fill={getColor(variantColor).secondary}
          ></circle>
        </g>
      </HorizontalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle06"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}

export function HorizontalRectangle07({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 7,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 7,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={7}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2067_3687)">
          <path
            fill={getColor(variantColor).primary}
            style={style}
            fillRule="evenodd"
            d="m1048.64 27.854-378.3 378.302A171.844 171.844 0 0 0 652.146 628H0V0h1085.1a171.8 171.8 0 0 0-36.46 27.854"
            clipRule="evenodd"
          ></path>
          <path
            stroke={getColor(variantColor).curvedLine}
            style={style}
            opacity={getColor(variantColor).curvedLineOpacity}
            strokeWidth="150"
            d="m-424.834 637.562-293-507.491a209.808 209.808 0 0 1 76.865-286.865l336.018-194a209.81 209.81 0 0 1 286.866 76.865l292.999 507.491a209.805 209.805 0 0 1-76.865 286.865l-336.018 194a209.81 209.81 0 0 1-286.865-76.865Z"
            clipRule="evenodd"
          ></path>
          <path
            fill={getColor(variantColor).quaternary}
            style={style}
            d="m708.711-173.568 443.399-256a115.893 115.893 0 0 1 158.46 42.459l168 290.984a115.88 115.88 0 0 1 11.63 88.034 115.86 115.86 0 0 1-54.09 70.425l-69.57 40.169-2.57-2.563 67.83-39.122a114.42 114.42 0 0 0 53.45-69.52 114.35 114.35 0 0 0-11.49-86.904l-166.01-287.248a114.51 114.51 0 0 0-69.59-53.396 114.62 114.62 0 0 0-86.99 11.482L713.023-172.055a114.42 114.42 0 0 0-53.449 69.52 114.3 114.3 0 0 0 11.493 86.905l154.217 266.843-3.342 3.341-155.69-269.663a115.895 115.895 0 0 1 42.459-158.459"
            opacity="0.102"
          ></path>
          <path
            fill={getColor(variantColor).secondary}
            style={style}
            d="m986.375 8.125-144.25 144.25a129.88 129.88 0 0 0-35.59 117.464L1039.02 37.353a171.87 171.87 0 0 1 104.8-49.617 129.882 129.882 0 0 0-157.445 20.39"
          ></path>
          <path
            fill={getColor(variantColor).quaternary}
            style={style}
            d="m616.672-258.994 507.488-293a209.808 209.808 0 0 1 286.87 76.865l194 336.018a209.8 209.8 0 0 1 21.05 159.373 209.78 209.78 0 0 1-97.92 127.492l-73.78 42.597-2.73-2.733 72.45-41.804a207.8 207.8 0 0 0 97.05-126.277 207.7 207.7 0 0 0-20.87-157.853L1408.01-471.13c-27.56-47.813-73.02-82.706-126.35-96.988a208.08 208.08 0 0 0-157.96 20.855L620.736-257.056a207.82 207.82 0 0 0-97.052 126.276 207.7 207.7 0 0 0 20.867 157.854l184.867 320.004-3.376 3.376-186.24-322.583a209.81 209.81 0 0 1 76.87-286.865"
            opacity="0.102"
          ></path>
        </g>
      </HorizontalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle07"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}

export function HorizontalRectangle08({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 8,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 8,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={8}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2060_5659)">
          <path
            style={style}
            fill={getColor(variantColor).primary}
            fillRule="evenodd"
            d="M1200 0H0v628h450.637C510.011 336.277 811.771 103.001 1200 43.08z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity={getColor(variantColor).fillOpacity}
            fillRule="evenodd"
            d="M285 741c0 122.158 29.56 237.413 81.917 339H1685.08c52.36-101.587 81.92-216.842 81.92-339 0-409.243-331.76-741-741-741-409.243 0-741 331.757-741 741m940 338H564.97C485.406 976.662 440 858.445 440 732.522 440 389.099 777.721 102.994 1225 40z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M-23 562c-39.7 0-72 29.608-72 66s32.3 66 72 66 72-29.608 72-66-32.3-66-72-66m0 187c-72.784 0-132-54.281-132-121s59.216-121 132-121 132 54.281 132 121S49.784 749-23 749"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            d="M244.155 581.216a15.999 15.999 0 0 1 25.484-14.987 16 16 0 0 1-7.423 28.617c-8.751 1.223-16.838-4.879-18.061-13.63M618.556 129.833a4.15 4.15 0 0 1 4.166-4.166 4.15 4.15 0 0 1 4.167 4.166 4.15 4.15 0 0 1-4.167 4.167 4.15 4.15 0 0 1-4.166-4.167M733.833 134a4.15 4.15 0 0 0 4.167-4.167 4.15 4.15 0 0 0-4.167-4.166 4.15 4.15 0 0 0-4.166 4.166 4.15 4.15 0 0 0 4.166 4.167m-166.666-8.333a4.15 4.15 0 0 0-4.167 4.166 4.15 4.15 0 0 0 4.167 4.167 4.15 4.15 0 0 0 4.166-4.167 4.15 4.15 0 0 0-4.166-4.166M678.278 134a4.15 4.15 0 0 0 4.166-4.167 4.15 4.15 0 0 0-4.166-4.166 4.15 4.15 0 0 0-4.167 4.166 4.15 4.15 0 0 0 4.167 4.167m55.555-41.667A4.15 4.15 0 0 0 738 88.167 4.15 4.15 0 0 0 733.833 84a4.15 4.15 0 0 0-4.166 4.167 4.15 4.15 0 0 0 4.166 4.166m-55.555 0a4.15 4.15 0 0 0 4.166-4.166A4.15 4.15 0 0 0 678.278 84a4.15 4.15 0 0 0-4.167 4.167 4.15 4.15 0 0 0 4.167 4.166m-55.556 0a4.15 4.15 0 0 0 4.167-4.166A4.15 4.15 0 0 0 622.722 84a4.15 4.15 0 0 0-4.166 4.167 4.15 4.15 0 0 0 4.166 4.166m-55.555 0a4.15 4.15 0 0 0 4.166-4.166A4.15 4.15 0 0 0 567.167 84 4.15 4.15 0 0 0 563 88.167a4.15 4.15 0 0 0 4.167 4.166"
          ></path>
        </g>
      </HorizontalRectangleMask>
      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle08"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}

export function HorizontalRectangle09({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 9,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 9,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={9}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2060_5678)">
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillRule="evenodd"
            d="M727.2 628 297.141 314 727.2 0H0v628z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).primary}
            fillRule="evenodd"
            d="M473.732 730 756 314.001 473.732-102H0v832z"
            clipRule="evenodd"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity={getColor(variantColor).triangleOpacity}
            d="M342.655 105.359 303 131.718V79zM229.001 533.359l39.655 26.359V507z"
          ></path>
        </g>
      </HorizontalRectangleMask>

      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle09"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}

export function HorizontalRectangle10({
  size = 1200,
  variantColor,
  primaryText,
  secondaryText,
  callText,
  imageUrl,
  logoSrc,
  positions,
  x_axis = 50,
}) {
  const intl = useIntl();

  const label = intl.formatMessage({
    id: "image",
  });

  const horizontalRectangleSizes = pickHorizontalRectangleDefaultSizes({
    shape: 10,
    size,
  });
  const horizontalRectanglePositions =
    positions ??
    pickHorizontalRectangleDefaultPositions({
      shape: 10,
      size,
    });

  return (
    <HorizontalRectangleContainer size={size} label={label} shape={10}>
      <HorizontalRectangleMask
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size / ratio}
        fill="none"
        viewBox="0 0 1200 628"
      >
        <g clipPath="url(#clip0_2033_5827)">
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity="0.2"
            d="M135.655 212.359 96 238.718V186z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).primary}
            d="M805.2 1001H0V0h539.431z"
          ></path>
          <path
            style={style}
            fill={getColor(variantColor).secondary}
            fillOpacity={getColor(variantColor).triangleOpacity}
            d="M236.2 1213H-569V212h539.431z"
          ></path>
        </g>
      </HorizontalRectangleMask>
      <Logo
        logoSrc={logoSrc}
        size={horizontalRectangleSizes.logoSize}
        top={horizontalRectanglePositions.logoPosition.y}
        left={horizontalRectanglePositions.logoPosition.x}
      />

      {imageUrl && (
        <HorizontalRectangleImage
          src={imageUrl}
          x_axis={x_axis}
          alt="horizontalRectangle10"
          top={horizontalRectanglePositions.imagePosition.y}
          left={horizontalRectanglePositions.imagePosition.x}
          size={horizontalRectangleSizes.imageSize}
        />
      )}

      <Labels
        top={horizontalRectanglePositions.labelsPosition.y}
        left={horizontalRectanglePositions.labelsPosition.x}
        callBackground={getColor(variantColor).secondary}
        callText={callText}
        callTextColor={getColor(variantColor).callTextColor}
        primaryText={primaryText}
        secondaryText={secondaryText}
        size={horizontalRectangleSizes.labelsSize}
        textColor={getColor(variantColor).textColor}
      />
    </HorizontalRectangleContainer>
  );
}
